import type { FormConfig } from '../../config/types';
import type { FetchFormResponse, RewardCosAuditResponse } from '../../service/types';
import { api } from '../../utils/data-utils';

export type ModerationPeriod = 'Q2' | 'Q4';

export type ModerationRow = {
  formId: string;
  period: ModerationPeriod;
  reportYear: string;
  scsName: string;
  scsEmail: string;
  lineManager: string;
  grade: string;
  temporaryGrade?: string;
  indicativeMarking: string;
  indicativePosition: string;
  managerAssessmentComplete: boolean;
  moderationComplete: boolean;
  finalMarking?: string;
  finalPosition?: string;
};

export const getModerationGrade = (row: ModerationRow) => row.temporaryGrade || row.grade;

export type ModerationDecision = {
  complete: boolean;
  finalMarking?: string;
  finalPosition?: string;
};

export type ModerationReportMetadata = {
  form_id: string;
  identifier?: string | null;
  form_state?: string | null;
  created_at?: string | null;
  submitted_at?: string | null;
  revision_version?: number | null;
  report_period?: string | null;
  pending_approval_quarter?: string | null;
  approved_quarter?: string | null;
  approval_status?: string | null;
};

export type ModerationPreview = {
  formConfig: FormConfig;
  formData: FetchFormResponse;
  reportMetadata?: ModerationReportMetadata;
};

const request = async <T>(token: string, action: string, body = {}): Promise<T> => {
  const response = await api(token, '/form', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Tenant': 'scs' },
    body: JSON.stringify({ action, ...body }),
  });
  return response.json();
};

export const fetchModerationAccess = (token: string) =>
  request<FetchFormResponse>(token, 'scs_moderation_access');

export const fetchModerationForms = async (token: string) =>
  (await request<{ forms: ModerationRow[] }>(token, 'scs_moderation_list')).forms;

export const saveModeration = (token: string, row: ModerationRow, decision: ModerationDecision) =>
  request<ModerationRow>(token, 'scs_moderation_complete', {
    formId: row.formId,
    period: row.period,
    ...decision,
  });

export const fetchModerationPreview = (token: string, row: ModerationRow) =>
  request<ModerationPreview>(token, 'scs_moderation_preview', {
    formId: row.formId,
    period: row.period,
  });

export const fetchModerationAudit = (token: string, row: ModerationRow) =>
  request<RewardCosAuditResponse>(token, 'scs_moderation_audit', {
    formId: row.formId,
    period: row.period,
  });
